import { inject, Injectable } from "@angular/core";
import { TaxesRemote } from "@modules/taxes/domain/infrastructure/taxes.remote";
import { TaxesMultiDeclarationStateService } from "@modules/taxes/domain/+state/taxes-multi-declaration.state.service";
import { map, take } from "rxjs/operators";
import { combineLatest, Observable } from "rxjs";
import {
	TaxesMultiDeclarationModel,
	TaxesRouteNames,
	TaxesUndeclaredModel,
	TaxesUndeclaredSingleModel,
	TaxesUndeclaredTrxModel
} from "@modules/taxes/domain/entities/taxes.model";
import { Dictionary } from "@helpers/types";
import { TaxesQueryParamName, TaxesQueryParamsCollection } from "@modules/taxes/domain/entities/taxes-navigation.model";
import { DepositCellEvent } from "@modules/taxes/features/taxes-multi-declaration/deposit-list/deposit-cell/deposit-cell.component";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { TaxesUndeclaredTrxFacade } from "@modules/taxes/domain/+state/taxes-undeclared-trx.facade";
import { TaxesNavigationService } from "@modules/taxes/domain/infrastructure/taxes-navigation.service";
import { ModalService } from "@core-services/modal.service";

interface MultiDeclarationViewModel {
	apiError: boolean;
	apiLoading: boolean;
	trxDictionary: Dictionary<TaxesUndeclaredModel[]>;
	trxLength: number;
}

@Injectable()
export class TaxesMultiDeclarationFacade {
	private readonly taxesUndeclaredTrxFacade = inject(TaxesUndeclaredTrxFacade);
	private readonly navigationService = inject(TaxesNavigationService);
	private readonly cryptoWalletService = inject(CryptoWalletService);
	private readonly state = inject(TaxesMultiDeclarationStateService);
	private readonly modalService = inject(ModalService);
	private readonly taxesRemote = inject(TaxesRemote);

	public getMultiDeclarationViewModel$: Observable<MultiDeclarationViewModel> = combineLatest([
		this.state.hasError$,
		this.state.isLoading$,
		this.state.trxDictionary$
	]).pipe(
		map(([hasError, isLoading, trxDictionary]) => ({
			apiError: hasError,
			apiLoading: isLoading,
			trxDictionary: trxDictionary,
			trxLength: Object.values(trxDictionary).flat().length
		}))
	);

	public getApiFail(): boolean {
		return this.state.hasError();
	}

	private getTrxDictionary(trx: TaxesMultiDeclarationModel): Dictionary<TaxesUndeclaredModel[]> {
		const transactionsDictionary: Dictionary<TaxesUndeclaredModel[]> = {};

		trx.transactions.forEach((tx) => {
			const trxKeyDate = tx.creditedAt.split("T")[0];

			if (!transactionsDictionary[trxKeyDate]) {
				transactionsDictionary[trxKeyDate] = [];
			}
			transactionsDictionary[trxKeyDate].push(tx);
		});

		return transactionsDictionary;
	}

	public fetchUndeclaredTransactions(
		reFetch?: boolean,
		trxInfo?: DepositCellEvent,
		isFromTrxDetails?: "true" | "false"
	): void {
		if (reFetch) {
			this.state.setIsLoading(true);
		}
		this.navigationService
			.routeParamsReader()
			.pipe(take(1))
			.subscribe((params) => {
				const walletId =
					params[TaxesQueryParamName.LeftSelectionWalletId] ?? params[TaxesQueryParamName.AssetWalletId];
				this.taxesRemote.getUndeclaredTransactions$(walletId).subscribe({
					next: (response) => {
						this.state.updateState({
							hasError: false,
							isLoading: false,
							trxDictionary: this.getTrxDictionary(response)
						});
						isFromTrxDetails === "true" ? this.setTrxData(trxInfo, isFromTrxDetails) : null;
						// On the buy flow if there are no transactions (for the current wallet) redirect to the success page
						if (params.fromTradeFlow && response.transactions.length === 0) {
							this.navigationService.gotoByNavigationUrl(TaxesRouteNames.Success, params);
						}
					},
					error: () => {
						this.state.taxesMultiErrorState();
					}
				});
			});
	}

	public setTrxData(
		event: DepositCellEvent,
		isFromTrxDetails: "true" | "false",
		path?: TaxesRouteNames,
		variant?: string
	): void {
		const walletId = event.walletId;
		const trxId = event.transactionPid;

		combineLatest([
			this.cryptoWalletService.getTransaction(walletId, trxId),
			this.state.trxDictionary$,
			this.navigationService.routeParamsReader()
		])
			.pipe(
				take(1),
				map(([trx, trxDictionary, query]) => {
					const trxData = Object.values(trxDictionary)
						.flat()
						.find((trx) => trx.transactionId === trxId);

					// Build the model with the information user selected
					const undeclaredTrx: TaxesUndeclaredTrxModel = {
						transaction_id: trxId,
						amount: trxData.assetAmount.toString()
					};

					const trxModel: TaxesUndeclaredSingleModel = {
						...trxData,
						walletTo: trx.getRecipient(),
						amountEuro: trx.getAmountEuro(),
						networkId: trx.getTxId()
					};

					trxModel && this.taxesUndeclaredTrxFacade.setSingleUndeclaredTrxDataState(undeclaredTrx, trxModel);
					return query;
				})
			)
			.subscribe((query) => {
				if (query.fromTradeFlow) {
					// Navigate to the next modal and write the QP
					this.onSubscriptionSuccess(isFromTrxDetails, path, variant, { ...query });
				} else {
					// Close trx detail modal and redirect user to the next screen
					this.onSubscriptionSuccess(isFromTrxDetails, path, variant);
				}
			});
	}

	private onSubscriptionSuccess(
		isFromTrxDetails: "true" | "false",
		path?: TaxesRouteNames,
		variant?: string,
		query?: Partial<TaxesQueryParamsCollection>
	): void {
		// Close the modal on transaction history screen
		this.modalService.popAll();

		// Update Navigation
		switch (path) {
			case TaxesRouteNames.SingleDeposit:
				this.navigateToSingleDeposit(isFromTrxDetails);
				break;

			case TaxesRouteNames.Confirmation:
				this.navigateToConfirmation(variant, "false");
				break;

			default:
				!path ? this.navigateToSingleDeposit(isFromTrxDetails) : null;
				break;
		}
	}

	public navigateToConfirmation(
		variant: string,
		isFromTrxDetails: "true" | "false",
		query?: Partial<TaxesQueryParamsCollection>
	): void {
		this.navigationService.updateQueryParamsAndNavigate(TaxesRouteNames.Confirmation, {
			...query,
			NavigationFrom: TaxesRouteNames.CryptoDeclaration,
			NavigationVariant: variant,
			FromTrxDetails: isFromTrxDetails
		});
	}

	private navigateToSingleDeposit(isFromTrxDetails: "true" | "false"): void {
		this.navigationService.updateQueryParamsAndNavigate(TaxesRouteNames.SingleDeposit, {
			FromTrxDetails: isFromTrxDetails
		});
	}
}
